// pages/public/PublicBooking.jsx
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import api, { bookings as bookingsApi, rooms as roomsApi, gmSkills as gmSkillsApi, stripeApi } from '../../utils/api';
import EmbeddedStripeCheckout from '../../components/EmbeddedStripeCheckout';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek,
         isSameMonth, isToday, isBefore, startOfDay, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Users, Clock, Star, CheckCircle2, Plus, Minus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import BrandIcon from '../../components/BrandIcon';

const WEEK_DAYS = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];
// Les créneaux viennent de la configuration admin (room.time_slots)

// ── Stepper ──────────────────────────────────────────────────
function StepIndicator({ step }) {
  const steps = ['Salle','Créneau','Joueurs','Paiement','Confirmation'];
  return (
    <div style={{ display:'flex', alignItems:'center', gap:0, marginBottom:36 }}>
      {steps.map((s, i) => (
        <div key={s} style={{ display:'flex', alignItems:'center', flex: i < steps.length-1 ? 1 : 0 }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
            <div style={{
              width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:13, fontWeight:700, transition:'all 0.2s',
              background: step > i ? 'var(--teal)' : step === i ? 'var(--accent)' : 'var(--bg-elevated)',
              color: step >= i ? '#fff' : 'var(--text-3)',
              boxShadow: step === i ? '0 0 16px var(--accent-glow)' : 'none',
            }}>
              {step > i ? <CheckCircle2 size={16}/> : i+1}
            </div>
            <span style={{ fontSize:11, color: step >= i ? 'var(--text-1)' : 'var(--text-3)', whiteSpace:'nowrap', fontWeight: step===i ? 600 : 400 }}>{s}</span>
          </div>
          {i < steps.length-1 && (
            <div style={{ flex:1, height:1, background: step > i ? 'var(--teal)' : 'var(--border)', margin:'0 8px', marginBottom:18, transition:'background 0.3s' }}/>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Carte salle ──────────────────────────────────────────────
function RoomCard({ room, selected, onSelect }) {
  return (
    <div onClick={() => onSelect(room)} style={{
      background: selected ? 'rgba(124,92,252,0.12)' : 'var(--bg-card)',
      border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius:14, overflow:'hidden', cursor:'pointer', transition:'all 0.2s',
      boxShadow: selected ? '0 0 24px var(--accent-glow)' : 'none',
      transform: selected ? 'scale(1.02)' : 'scale(1)',
    }}>
      {room.image_url
        ? <img src={room.image_url} alt={room.name} style={{ width:'100%', height:160, objectFit:'cover' }}/>
        : <div style={{ width:'100%', height:160, background:'linear-gradient(135deg,var(--accent-glow),var(--teal-glow))', display:'flex', alignItems:'center', justifyContent:'center' }}><BrandIcon size={48}/></div>
      }
      <div style={{ padding:18 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
          <h3 style={{ fontSize:17, fontWeight:700 }}>{room.name}</h3>
          <div style={{ display:'flex', gap:1 }}>
            {Array.from({length:5},(_,i)=>(
              <Star key={i} size={12} fill={i<(room.difficulty||0)?'var(--amber)':'none'} color={i<(room.difficulty||0)?'var(--amber)':'var(--text-3)'}/>
            ))}
          </div>
        </div>
        <p style={{ fontSize:13, color:'var(--text-2)', marginBottom:12, lineHeight:1.5 }}>
          {room.description || 'Une aventure unique vous attend…'}
        </p>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
          <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:'var(--teal)' }}>
            <Clock size={12}/> {room.duration_min} min
          </span>
          <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:'var(--text-2)' }}>
            <Users size={12}/> {room.min_players}–{room.max_players} joueurs
          </span>
        </div>
        {(room.prices||[]).filter(Boolean).length > 0 && (
          <div style={{ borderTop:'1px solid var(--border)', paddingTop:10 }}>
            {(room.prices||[]).filter(Boolean).map(p => (
              <div key={p.price_profile_id} style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:2 }}>
                <span style={{ color:'var(--text-2)' }}>{p.profile_name}</span>
                <span style={{ fontWeight:600, color:'var(--accent-light)' }}>
                  {(() => {
                    const t0 = (p.tiers||[])[0];
                    if (!t0) return '—';
                    if (t0.price_flat) return `${parseFloat(t0.price_flat).toFixed(2)} €`;
                    return `dès ${parseFloat(t0.price_per_person).toFixed(2)} €/pers`;
                  })()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Calendrier ───────────────────────────────────────────────
function CalendarPicker({ selectedDate, onSelect, bookedSlots, roomSlots }) {
  const [viewMonth, setViewMonth] = useState(new Date());
  const today = startOfDay(new Date());
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(viewMonth), { weekStartsOn:1 }),
    end:   endOfWeek(endOfMonth(viewMonth),     { weekStartsOn:1 }),
  });

  // Vrai si ce jour a au moins un créneau configuré
  const hasSlotOnDay = (d) => {
    if (!roomSlots || roomSlots.length === 0) return true; // pas de config = tous les jours ok
    const dow = d.getDay(); // 0=dim … 6=sam
    return roomSlots.some(s => !s.day_of_week || s.day_of_week.length === 0 || s.day_of_week.includes(dow));
  };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <button className="btn btn-ghost" onClick={() => setViewMonth(m => subMonths(m,1))}><ChevronLeft size={18}/></button>
        <span style={{ fontWeight:600 }}>{format(viewMonth,'MMMM yyyy',{locale:fr})}</span>
        <button className="btn btn-ghost" onClick={() => setViewMonth(m => addMonths(m,1))}><ChevronRight size={18}/></button>
      </div>
      <div className="cal-grid" style={{ marginBottom:6 }}>
        {WEEK_DAYS.map(d => <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:600, color:'var(--text-3)', paddingBottom:6 }}>{d}</div>)}
      </div>
      <div className="cal-grid">
        {days.map(day => {
          const ds = format(day,'yyyy-MM-dd');
          const past  = isBefore(day, today);
          const other = !isSameMonth(day, viewMonth);
          const sel   = selectedDate === ds;
          const slots = bookedSlots?.filter(b => b.session_date?.slice(0,10) === ds) || [];
          // Filtrer les créneaux selon le jour de la semaine du calendrier
          const dow = day.getDay(); // 0=dim, 1=lun … 6=sam
          const daySlots = (roomSlots || []).filter(rs => {
            const days = rs.day_of_week;
            if (!days || days.length === 0) return true;
            return days.includes(dow);
          });
          const allFull = daySlots.length > 0 &&
            daySlots.every(rs => slots.some(b => b.session_start?.slice(0,5) === rs.start_time?.slice(0,5)));
          const noSlots = !hasSlotOnDay(day); // jour sans créneau configuré
          return (
            <div key={ds}
              className={`cal-day${isToday(day)?' today':''}${sel?' selected':''}${past||other||allFull||noSlots?' disabled':''}${slots.length>0&&!allFull&&!noSlots?' has-slots':''}`}
              onClick={() => !past && !other && !allFull && !noSlots && onSelect(ds)}
              title={noSlots ? 'Fermé ce jour' : undefined}>
              {format(day,'d')}
            </div>
          );
        })}
      </div>
      <div style={{ display:'flex', gap:16, marginTop:12, fontSize:12, color:'var(--text-3)' }}>
        <span><span style={{ width:7,height:7,borderRadius:'50%',background:'var(--teal)',display:'inline-block',marginRight:5 }}/>Créneaux dispo</span>
        <span><span style={{ width:7,height:7,borderRadius:'50%',background:'var(--accent)',display:'inline-block',marginRight:5 }}/>Sélectionné</span>
      </div>
    </div>
  );
}

// ── Modale "Appeler pour réserver" ───────────────────────────
function CallModal({ slot, phone, onClose }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:300, padding:16, backdropFilter:'blur(4px)' }}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:18,
        padding:32, maxWidth:400, width:'100%', textAlign:'center', boxShadow:'0 8px 40px rgba(0,0,0,0.5)' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>📞</div>
        <h3 style={{ fontSize:20, fontWeight:700, marginBottom:8 }}>Réservation par téléphone</h3>
        <p style={{ color:'var(--text-2)', fontSize:14, marginBottom:24, lineHeight:1.6 }}>
          Ce créneau (<strong>{slot}</strong>) est dans moins d'une heure.<br/>
          Pour réserver, veuillez nous appeler directement.
        </p>
        {phone && (
          <a href={`tel:${phone.replace(/\s/g,'')}`}
            style={{ display:'inline-flex', alignItems:'center', gap:10, background:'var(--teal)',
              color:'#fff', fontWeight:700, fontSize:18, padding:'14px 28px', borderRadius:12,
              textDecoration:'none', marginBottom:16 }}>
            📞 {phone}
          </a>
        )}
        {!phone && (
          <div style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 20px',
            color:'var(--text-2)', fontSize:14, marginBottom:16 }}>
            Contactez-nous par téléphone pour finaliser votre réservation.
          </div>
        )}
        <div>
          <button onClick={onClose} style={{ background:'none', border:'1px solid var(--border)',
            borderRadius:8, padding:'8px 20px', color:'var(--text-2)', cursor:'pointer',
            fontSize:14, fontFamily:'inherit' }}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sélecteur de créneau ─────────────────────────────────────
function SlotPicker({ date, room, onSelect, selectedSlot, bookedSlots, cutoffMin, businessPhone, slotStatuses }) {
  const [callSlot, setCallSlot] = useState(null);
  const taken = bookedSlots?.filter(b => b.session_date?.slice(0,10) === date).map(b => b.session_start?.slice(0,5)) || [];

  const dayOfWeek = new Date(date + 'T12:00:00').getDay();
  const isToday   = date === new Date().toISOString().slice(0,10);
  const nowMinutes = isToday ? new Date().getHours() * 60 + new Date().getMinutes() : -1;
  const CUTOFF = cutoffMin ?? 30; // délai configurable, défaut 30 min

  const activeSlots = (room.time_slots || [])
    .filter(s => {
      const days = s.day_of_week;
      if (!days || days.length === 0) return true;
      return days.includes(dayOfWeek);
    })
    .map(s => s.start_time?.slice(0,5))
    .filter(Boolean)
    .sort();
  const slotsToShow = activeSlots.length > 0
    ? activeSlots
    : ['10:00','11:30','13:00','14:30','16:00','17:30','19:00','20:30'];

  const slotMinutes = (slot) => { const [h,m] = slot.split(':').map(Number); return h*60+m; };
  const isPast     = (slot) => nowMinutes >= 0 && slotMinutes(slot) <= nowMinutes;
  const isCallOnly = (slot) => nowMinutes >= 0 && !isPast(slot) && (slotMinutes(slot) - CUTOFF) <= nowMinutes;

  const handleClick = (slot) => {
    if (isPast(slot) || taken.includes(slot)) return;
    const gmStatus = slotStatuses?.[slot];
    if (gmStatus?.blocked) return; // bloqué sans GM
    if (isCallOnly(slot) || gmStatus?.call) { setCallSlot(slot); return; }
    onSelect(slot);
  };

  return (
    <div>
      <div style={{ marginBottom:14, fontSize:14, color:'var(--text-2)' }}>
        {format(new Date(date), "EEEE d MMMM", {locale:fr})} — <strong>{room.name}</strong>
      </div>
      {CUTOFF > 0 && (
        <div style={{ fontSize:12, color:'var(--text-3)', marginBottom:12, display:'flex', alignItems:'center', gap:6 }}>
          <span>ℹ</span> Les créneaux dans moins de {CUTOFF} min nécessitent un appel téléphonique.
        </div>
      )}
      {activeSlots.length === 0 && (
        <p style={{ fontSize:12, color:'var(--amber)', marginBottom:10 }}>
          ⚠ Aucun créneau configuré — créneaux par défaut affichés
        </p>
      )}
      <div className="slots-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))', gap:8 }}>
        {slotsToShow.map(slot => {
          const taken_    = taken.includes(slot);
          const past_     = isPast(slot);
          const call_     = isCallOnly(slot);
          const sel_      = selectedSlot === slot;
          const gmSt      = slotStatuses?.[slot];
          const blocked_  = gmSt?.blocked && gmSt?.override !== 'unblock';
          const callGM_   = gmSt?.call && !blocked_;
          const disabled  = taken_ || past_ || blocked_;

          let bg     = 'var(--bg-elevated)';
          let border = 'var(--border)';
          let color  = 'var(--text-1)';
          let opacity = 1;
          if (sel_)          { bg = 'var(--accent-glow)'; border = 'var(--accent)'; color = 'var(--accent-light)'; }
          else if (past_)    { bg = 'rgba(255,255,255,0.02)'; border = 'rgba(255,255,255,0.04)'; color = 'var(--text-3)'; opacity = 0.35; }
          else if (taken_)   { bg = 'rgba(255,255,255,0.02)'; border = 'rgba(255,255,255,0.04)'; color = 'var(--text-3)'; opacity = 0.5; }
          else if (blocked_) { bg = 'rgba(255,77,109,0.06)'; border = 'rgba(255,77,109,0.2)'; color = 'var(--text-3)'; opacity = 0.45; }
          else if (call_ || callGM_) { bg = 'rgba(245,166,35,0.08)'; border = 'rgba(245,166,35,0.3)'; color = 'var(--amber)'; }

          // Label unique selon priorité
          const label = past_ ? 'Passé'
            : taken_ ? 'Complet'
            : blocked_ ? 'Indisponible'
            : sel_ ? 'Sélectionné ✓'
            : (call_ || callGM_) ? '📞 Appeler'
            : null;

          const labelColor = past_ ? 'var(--text-3)'
            : taken_ ? 'var(--text-3)'
            : blocked_ ? 'var(--red)'
            : sel_ ? 'var(--accent-light)'
            : 'var(--amber)';

          return (
            <div key={slot} onClick={() => handleClick(slot)} style={{
              padding:'12px 10px', textAlign:'center', borderRadius:10,
              border:`1px solid ${border}`, background: bg, color,
              cursor: disabled || past_ ? 'not-allowed' : 'pointer',
              fontWeight: sel_ ? 700 : 500, fontSize:16, fontFamily:'monospace',
              transition:'all 0.15s', opacity,
              textDecoration: taken_ ? 'line-through' : 'none',
            }}>
              {slot}
              {label && <div style={{ fontSize:10, marginTop:3, color:labelColor }}>{label}</div>}
            </div>
          );
        })}
      </div>
      {callSlot && (
        <CallModal slot={callSlot} phone={businessPhone} onClose={() => setCallSlot(null)}/>
      )}
    </div>
  );
}

// ── Formatage téléphone ───────────────────────────────────────
function formatPhone(val) {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
}

// ── Helpers tarif ─────────────────────────────────────────────
const CHILD_KEYWORDS = ['enfant', 'child', 'junior'];
const isChild = (name) => CHILD_KEYWORDS.some(k => (name||'').toLowerCase().includes(k));

function calcPricing(profiles, qtys) {
  const totalPlayers = Object.values(qtys).reduce((s, q) => s + q, 0);
  let total = 0;
  const breakdown = [];
  const onlyChildren = profiles
    .filter(p => (qtys[p.price_profile_id]||0) > 0)
    .every(p => isChild(p.profile_name));

  for (const p of profiles) {
    const qty = qtys[p.price_profile_id] || 0;
    if (qty <= 0) continue;
    const tiers = (p.tiers||[]).filter(t => totalPlayers >= (t.min_players||1));
    const tier  = tiers.length > 0 ? tiers[tiers.length-1] : (p.tiers||[])[0];
    const lineTotal = tier?.price_flat
      ? parseFloat(tier.price_flat)
      : (parseFloat(tier?.price_per_person||0) * qty);
    total += lineTotal;
    breakdown.push({ profileId: p.price_profile_id, profileName: p.profile_name, qty, lineTotal });
  }
  return { total: total.toFixed(2), totalPlayers, breakdown, onlyChildren };
}

// ── Sélecteur multi-profils joueurs (public) ─────────────────
function PublicPlayerPicker({ room, onChange }) {
  const profiles = (room?.prices || []).filter(Boolean);
  // Tous à 0 par défaut
  const [qtys, setQtys] = useState({});

  useEffect(() => {
    if (profiles.length > 0) {
      const init = {};
      for (const p of profiles) init[p.price_profile_id] = 0;
      setQtys(init);
      onChange(calcPricing(profiles, init));
    }
  // eslint-disable-next-line
  }, [room?.id]);

  const upd = (profileId, delta) => {
    setQtys(prev => {
      const next = { ...prev, [profileId]: Math.max(0, (prev[profileId]||0) + delta) };
      onChange(calcPricing(profiles, next));
      return next;
    });
  };

  const totalPlayers = Object.values(qtys).reduce((s,q) => s+q, 0);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
      {profiles.map(p => {
        const qty = qtys[p.price_profile_id] || 0;
        const tiers = (p.tiers||[]).filter(t => Math.max(1,totalPlayers) >= (t.min_players||1));
        const tier  = tiers.length > 0 ? tiers[tiers.length-1] : (p.tiers||[])[0];
        const unitPrice = tier?.price_flat ? null : parseFloat(tier?.price_per_person||0);
        const lineTotal = tier?.price_flat
          ? parseFloat(tier.price_flat)
          : unitPrice * qty;

        return (
          <div key={p.price_profile_id} style={{
            display:'flex', alignItems:'center', gap:12,
            padding:'12px 14px', borderRadius:10,
            background: qty > 0 ? 'rgba(124,92,252,0.08)' : 'var(--bg-elevated)',
            border: `1px solid ${qty > 0 ? 'rgba(124,92,252,0.2)' : 'var(--border)'}`,
            transition:'all 0.15s',
          }}>
            {/* Nom + prix unitaire */}
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:14 }}>{p.profile_name}</div>
              <div style={{ fontSize:12, color:'var(--text-3)' }}>
                {tier?.price_flat
                  ? `${parseFloat(tier.price_flat).toFixed(0)} € fixe`
                  : unitPrice ? `${unitPrice.toFixed(0)} €/pers` : '—'}
              </div>
            </div>

            {/* Stepper */}
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <button type="button"
                onClick={() => upd(p.price_profile_id, -1)}
                disabled={qty <= 0}
                style={{ width:32, height:32, borderRadius:8, border:'1px solid var(--border)',
                  background:'var(--bg-base)', color: qty>0 ? 'var(--text-1)' : 'var(--text-3)',
                  cursor: qty>0 ? 'pointer' : 'not-allowed', fontSize:18, fontWeight:300,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                −
              </button>
              <input
                type="number" min={0}
                value={qty}
                onChange={e => {
                  const v = Math.max(0, parseInt(e.target.value)||0);
                  setQtys(prev => {
                    const next = {...prev, [p.price_profile_id]: v};
                    onChange(calcPricing(profiles, next));
                    return next;
                  });
                }}
                style={{ width:40, textAlign:'center', background:'var(--bg-base)',
                  border:'1px solid var(--border)', borderRadius:8, color:'var(--text-1)',
                  fontSize:16, fontWeight:700, padding:'4px 0', fontFamily:'monospace' }}
              />
              <button type="button"
                onClick={() => upd(p.price_profile_id, 1)}
                style={{ width:32, height:32, borderRadius:8, border:'1px solid var(--border)',
                  background:'var(--bg-base)', color:'var(--text-1)',
                  cursor:'pointer', fontSize:18, fontWeight:300,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                +
              </button>
            </div>

            {/* Sous-total */}
            <span style={{ minWidth:60, textAlign:'right', fontWeight:700, fontSize:14,
              color: qty > 0 ? 'var(--teal)' : 'var(--text-3)' }}>
              {qty > 0 ? `${lineTotal.toFixed(0)} €` : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Formulaire réservation ───────────────────────────────────
function BookingForm({ room, date, slot, onSubmit, loading }) {
  const [customerName,  setName]  = useState('');
  const [customerEmail, setEmail] = useState('');
  const [customerPhone, setPhone] = useState('');
  const [otherPlayers, setOtherPlayers] = useState(''); // noms des autres joueurs, un par ligne (optionnel)
  const [giftCode, setGiftCode]   = useState('');
  const [giftCard, setGiftCard]   = useState(null);
  const [checkingGift, setCheckGift] = useState(false);
  const [pricing, setPricing]     = useState({ total:'0.00', totalPlayers:0, breakdown:[] });

  const totalFloat   = parseFloat(pricing.total) || 0;
  const minPlayers   = room.min_players || 1;
  const maxPlayers   = room.max_players || 20;
  const validGroup   = pricing.totalPlayers >= minPlayers && pricing.totalPlayers <= maxPlayers;

  const checkGift = async () => {
    if (!giftCode.trim()) return;
    setCheckGift(true);
    try {
      const r = await giftCardsApi.check(giftCode.trim());
      setGiftCard(r.data.giftCard);
    } catch(e) { setGiftCard(null); }
    finally { setCheckGift(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pricing.totalPlayers === 0) return toast.error('Ajoutez au moins un joueur');
    if (!validGroup) return toast.error(`Groupe entre ${minPlayers} et ${maxPlayers} joueurs`);
    if (pricing.onlyChildren) return toast.error('Un adulte (plein tarif, étudiant ou abonné) est requis dans le groupe');
    // Noms des autres joueurs (optionnel, un par ligne) : permet au
    // programme de fidélité de suivre chaque joueur individuellement, pas
    // seulement le contact qui a réservé. Le contact est toujours ajouté
    // automatiquement côté serveur si non répété ici.
    const otherPlayerNames = otherPlayers.split('\n').map(n => n.trim()).filter(Boolean);
    const players = [
      { firstName: customerName.trim().split(' ')[0] || customerName, lastName: customerName.trim().split(' ').slice(1).join(' ') || null, email: customerEmail, isContact: true },
      ...otherPlayerNames.map(n => ({ firstName: n.split(' ')[0] || n, lastName: n.split(' ').slice(1).join(' ') || null })),
    ];
    onSubmit({
      roomId: room.id, sessionDate: date, sessionStart: slot, source: 'online',
      customerName, customerEmail, customerPhone,
      playerCount: pricing.totalPlayers,
      totalAmount: pricing.total,
      priceBreakdown: JSON.stringify(pricing.breakdown),
      players,
      // pas de priceProfileId unique — on envoie le breakdown
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Récap */}
      <div style={{ marginBottom:20, padding:16, background:'var(--bg-elevated)', borderRadius:10 }}>
        <div style={{ fontSize:12, color:'var(--text-2)', marginBottom:4 }}>Récapitulatif</div>
        <div style={{ fontWeight:700, fontSize:16 }}>{room.name}</div>
        <div style={{ fontSize:14, color:'var(--text-2)', marginTop:2 }}>
          {format(new Date(date),"EEEE d MMMM",{locale:fr})} à <strong>{slot}</strong>
        </div>
      </div>

      {/* Infos client */}
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Votre nom *</label>
          <input className="input" required value={customerName} onChange={e=>setName(e.target.value)} placeholder="Jean Dupont"/>
        </div>
        <div className="form-group">
          <label className="form-label">Email *</label>
          <input type="email" className="input" required value={customerEmail} onChange={e=>setEmail(e.target.value)} placeholder="jean@exemple.fr"/>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Téléphone</label>
        <input type="tel" className="input" value={customerPhone} onChange={e=>setPhone(formatPhone(e.target.value))} placeholder="06 12 34 56 78"/>
      </div>
      <div className="form-group">
        <label className="form-label">
          Prénoms des autres joueurs
          <span className="text-xs text-muted" style={{marginLeft:8, fontWeight:400}}>(optionnel, un par ligne)</span>
        </label>
        <textarea className="input" rows={3} value={otherPlayers}
          onChange={e=>setOtherPlayers(e.target.value)}
          placeholder={"Marie\nLucas\n..."}/>
        <p style={{ fontSize:11, color:'var(--text-3)', marginTop:4 }}>
          Utile pour le programme de fidélité : chaque joueur cumule ses propres sessions.
        </p>
      </div>

      {/* Composition du groupe */}
      <div className="form-group">
        <label className="form-label" style={{marginBottom:8}}>
          Composition du groupe *
          <span className="text-xs text-muted" style={{marginLeft:8, fontWeight:400}}>
            ({minPlayers} à {maxPlayers} joueurs)
          </span>
        </label>
        <PublicPlayerPicker room={room} onChange={setPricing}/>
        {pricing.totalPlayers > 0 && !validGroup && (
          <p style={{ color:'var(--red)', fontSize:12, marginTop:6 }}>
            ⚠ Groupe de {pricing.totalPlayers} joueur(s) — entre {minPlayers} et {maxPlayers} requis
          </p>
        )}
        {/* Bon cadeau */}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', fontSize:13, fontWeight:600, marginBottom:6, color:'var(--text-2)' }}>
            🎁 Bon cadeau (optionnel)
          </label>
          <div style={{ display:'flex', gap:8 }}>
            <input className="input" style={{ flex:1 }} value={giftCode}
              onChange={e => { setGiftCode(e.target.value.toUpperCase()); setGiftCard(null); }}
              placeholder="EG-XXXXXXXX"/>
            <button type="button" className="btn btn-secondary" onClick={checkGift} disabled={checkingGift}>
              {checkingGift ? '...' : 'Vérifier'}
            </button>
          </div>
          {giftCard && (
            <div style={{ marginTop:6, padding:'8px 12px', background:'rgba(0,210,180,0.08)',
              border:'1px solid var(--teal)', borderRadius:8, fontSize:12, color:'var(--teal)' }}>
              ✓ {parseFloat(giftCard.remaining_amount).toFixed(2)} € disponible
            </div>
          )}
          {giftCode && !giftCard && !checkingGift && giftCode.length > 5 && (
            <div style={{ marginTop:4, fontSize:11, color:'var(--red)' }}>Code invalide ou expiré</div>
          )}
        </div>

        {pricing.onlyChildren && (
          <p style={{ color:'var(--amber)', fontSize:12, marginTop:6 }}>
            ⚠ Un adulte (plein tarif, étudiant ou abonné) est requis dans le groupe
          </p>
        )}
      </div>

      {/* Total */}
      {totalFloat > 0 && (
        <div style={{ textAlign:'center', padding:'16px', background:'var(--bg-elevated)', borderRadius:10, marginBottom:20 }}>
          <div style={{ fontSize:13, color:'var(--text-2)', marginBottom:4 }}>
            {pricing.breakdown.map(b => `${b.qty > 1 ? `${b.qty}× ` : ''}${b.profileName}`).join(' + ')}
          </div>
          <div style={{ fontSize:34, fontWeight:700, color:'var(--teal)' }}>{totalFloat.toFixed(2)} €</div>
          <div style={{ fontSize:12, color:'var(--text-3)', marginTop:4 }}>Règlement sur place le jour J</div>
        </div>
      )}

      <button type="submit" className="btn btn-primary w-full"
        style={{ justifyContent:'center', padding:15, fontSize:16 }}
        disabled={loading || !validGroup || totalFloat === 0}>
        {loading ? <span className="spinner" style={{width:20,height:20}}/> : '🎮 Confirmer ma réservation'}
      </button>
    </form>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function PublicBooking() {
  const [step, setStep]          = useState(0);
  const [rooms, setRooms]        = useState([]);
  const [bookedSlots, setBooked] = useState([]);
  const [selectedRoom, setRoom]  = useState(null);
  const [addons, setAddons]       = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [payChoice, setPayChoice]   = useState(null);
  const [pendingFormData, setPendingForm] = useState(null);
  const [selectedDate, setDate]  = useState(null);
  const [selectedSlot, setSlot]  = useState(null);
  const [booking, setBooking]    = useState(null);
  const [loading, setLoading]    = useState(false);
  const [cutoffMin, setCutoff]   = useState(30);
  const [slotStatuses, setSlotStatuses] = useState({});
  const [searchParams] = useSearchParams();
  const { siteSlug } = useParams();

  // Détecter retour Stripe — avec le Checkout intégré (iframe), Stripe ne
  // redirige la page que lorsque le paiement a réellement abouti (un
  // paiement annulé/abandonné reste dans l'iframe, pas de redirection) :
  // un seul cas à gérer ici, plus de "cancelled".
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'return') {
      const ref = params.get('ref');
      window.history.replaceState({}, '', '/reserver');
      // Charger la réservation confirmée
      bookingsApi.getByRef(ref).then(r => {
        setBooking(r.data.booking);
        setStep(4);
        toast.success('Paiement confirmé ! 🎉');
      }).catch(() => toast.success(`Réservation ${ref} confirmée !`));
    }
  }, []);
  const [businessPhone, setPhone] = useState('');
  const [stripeEnabled, setStripe] = useState(false);
  const [stripePubKey, setStripePubKey] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);

  useEffect(() => {
    roomsApi.list(true).then(r => {
      let allRooms = r.data.rooms.filter(room => room.is_active);
      // Si on arrive depuis /reserver/:siteSlug, filtrer par site
      if (siteSlug) {
        allRooms = allRooms.filter(room => {
          // Comparer le slug du site ou le nom normalisé
          return room.site_slug === siteSlug || room.site_id;
        });
      }
      setRooms(allRooms);
      // Pré-sélectionner une salle si ?room=uuid dans l'URL
      const roomParam = searchParams?.get('room');
      if (roomParam) {
        const preselected = allRooms.find(r => r.id === roomParam);
        if (preselected) { setRoom(preselected); loadSlots(preselected.id, null); setStep(1); }
      }
    });
    // Charger les settings publics (téléphone, délai)
    stripeApi.config().then(r => {
      if (r.data.enabled) { setStripe(true); setStripePubKey(r.data.publishableKey); }
    }).catch(()=>{});
    api.get('/settings/public').then(r => {
      const s = r.data.settings || {};
      if (s.booking_cutoff_min) setCutoff(parseInt(s.booking_cutoff_min));
      if (s.business_phone) setPhone(s.business_phone);
    }).catch(() => {});
  }, []);

  const loadSlots = useCallback(async (roomId, date) => {
    try {
      const promises = [bookingsApi.public({ roomId })];
      if (date) promises.push(gmSkillsApi.slotStatus({ roomId, date }));
      const [bk, gm] = await Promise.all(promises);
      setBooked(bk.data.bookings);
      if (gm) setSlotStatuses(gm.data.statuses || {});
      else setSlotStatuses({});
    } catch {}
  }, []);

  const pickRoom = (room) => { setRoom(room); loadSlots(room.id, null); setDate(null); setSlot(null); setStep(1); };
  const pickDate = (date) => { setDate(date); setSlot(null); if (selectedRoom) loadSlots(selectedRoom.id, date); };
  const pickSlot = (slot) => { setSlot(slot); setStep(2); };

  // Étape 2→3 : enregistre les données, charge les add-ons
  const submit = async (formData) => {
    setPendingForm(formData);
    setStep(3);
    if (selectedRoom?.site_id) {
      roomsApi.addons(selectedRoom.site_id)
        .then(r => setAddons(r.data.addons || []))
        .catch(() => {});
    }
  };

  // Étape 3 → crée la réservation et redirige vers Stripe
  const confirmAndPay = async () => {
    if (!pendingFormData) return;
    setLoading(true);
    try {
      const r = await bookingsApi.create(pendingFormData);
      const bk = r.data.booking;
      setBooking(bk);

      const totalAmount   = parseFloat(bk.total_amount);
      // Acompte = 1 place adulte au tarif plein (premier tier du profil principal)
      // On cherche le prix unitaire dans le breakdown ou on prend totalAmount/players comme fallback
      let depositAmount = 10;
      if (pendingFormData?.priceBreakdown) {
        const bd = typeof pendingFormData.priceBreakdown === 'string'
          ? JSON.parse(pendingFormData.priceBreakdown)
          : pendingFormData.priceBreakdown;
        const adultProfile = bd.find(b =>
          b.profileName?.toLowerCase().includes('plein') ||
          b.profileName?.toLowerCase().includes('adulte')
        ) || bd[0];
        if (adultProfile) depositAmount = Math.max(parseFloat(adultProfile.lineTotal / adultProfile.qty), 10);
      } else {
        depositAmount = Math.max(parseFloat((totalAmount / (bk.player_count||1)).toFixed(2)), 10);
      }
      // Ajouter les add-ons si paiement complet
      const addonsTotal = selectedAddons.reduce((s, a) => s + (a.price || 0), 0);
      const amount = payChoice === 'full' ? totalAmount + addonsTotal : depositAmount;

      if (stripeEnabled) {
        setStripeLoading(true);
        try {
          const sr = await stripeApi.checkout({ bookingId: bk.id, amount });
          // Affiche le paiement en iframe sur cette même page, au lieu de
          // rediriger vers stripe.com.
          setCheckoutClientSecret(sr.data.clientSecret);
        } catch(e) {
          toast.error('Erreur paiement : ' + (e.response?.data?.error || e.message));
        } finally {
          setStripeLoading(false);
        }
      } else {
        // Pas de Stripe : afficher confirmation directement
        setStep(4);
      }
    } catch(e) {
      toast.error(e.response?.data?.error || 'Erreur lors de la réservation');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-base)', paddingBottom:60 }}>
      {/* Header */}
      <div style={{ background:'var(--bg-card)', borderBottom:'1px solid var(--border)', padding:'20px 32px', display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:40 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, display:'flex', alignItems:'center', gap:8 }}>
            <BrandIcon size={26}/>
            <span style={{ background:'linear-gradient(135deg,var(--accent-light),var(--teal))', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>EscapeOS</span>
          </h1>
          <span style={{ fontSize:13, color:'var(--text-3)' }}>Réservation en ligne</span>
        </div>
        <a href="/login" className="btn btn-secondary btn-sm">Espace staff</a>
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'0 20px' }}>
        <StepIndicator step={step}/>

        {/* Étape 0 — Salle */}
        {step === 0 && (
          <div>
            <h2 style={{ fontSize:22, fontWeight:700, marginBottom:6 }}>Choisissez votre aventure</h2>
            <p style={{ color:'var(--text-2)', marginBottom:24 }}>Sélectionnez une salle pour voir les disponibilités</p>
            <div className="rooms-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:20 }}>
              {rooms.map(room => <RoomCard key={room.id} room={room} selected={selectedRoom?.id===room.id} onSelect={pickRoom}/>)}
              {rooms.length === 0 && <div className="empty-state"><p>Chargement…</p></div>}
            </div>
          </div>
        )}

        {/* Étape 1 — Date + créneau */}
        {step === 1 && selectedRoom && (
          <div>
            <button className="btn btn-ghost btn-sm" style={{marginBottom:20}} onClick={() => setStep(0)}>
              <ChevronLeft size={14}/> Retour
            </button>
            <div className="booking-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
              <div className="card">
                <div className="card-title" style={{marginBottom:16}}>Choisissez une date</div>
                <CalendarPicker selectedDate={selectedDate} onSelect={pickDate} bookedSlots={bookedSlots} roomSlots={selectedRoom?.time_slots}/>
              </div>
              <div className="card">
                <div className="card-title" style={{marginBottom:16}}>
                  {selectedDate ? 'Choisissez un créneau' : 'Sélectionnez d\'abord une date'}
                </div>
                {selectedDate
                  ? <SlotPicker date={selectedDate} room={selectedRoom} onSelect={pickSlot} selectedSlot={selectedSlot} bookedSlots={bookedSlots} cutoffMin={cutoffMin} businessPhone={businessPhone} slotStatuses={slotStatuses}/>
                  : <div className="empty-state" style={{padding:'40px 0'}}><Clock size={28} style={{opacity:0.3}}/><p>Cliquez sur un jour</p></div>
                }
              </div>
            </div>
          </div>
        )}

        {/* Étape 2 — Formulaire */}
        {step === 2 && selectedRoom && selectedDate && selectedSlot && (
          <div>
            <button className="btn btn-ghost btn-sm" style={{marginBottom:20}} onClick={() => setStep(1)}>
              <ChevronLeft size={14}/> Retour
            </button>
            <div style={{ maxWidth:600, margin:'0 auto' }}>
              <div className="card">
                <h3 style={{ fontSize:18, fontWeight:700, marginBottom:20 }}>Vos informations</h3>
                <BookingForm room={selectedRoom} date={selectedDate} slot={selectedSlot} onSubmit={submit} loading={loading}/>
              </div>
            </div>
          </div>
        )}


        {/* Étape 3 — Add-ons & Paiement */}
        {step === 3 && pendingFormData && (
          <div style={{ maxWidth:560, margin:'0 auto' }}>
            <button className="btn btn-ghost btn-sm" style={{marginBottom:20}} onClick={() => setStep(2)}>
              <ChevronLeft size={14}/> Retour
            </button>

            {/* Récap */}
            <div className="card" style={{ marginBottom:16, padding:'14px 18px' }}>
              <div style={{ fontWeight:700, fontSize:14, marginBottom:8 }}>Récapitulatif</div>
              {[
                [selectedRoom?.name, `${format(new Date(pendingFormData.sessionDate),"EEE d MMM",{locale:fr})} à ${pendingFormData.sessionStart}`],
                ['Joueurs', pendingFormData.playerCount],
                ['Total', `${parseFloat(pendingFormData.totalAmount).toFixed(2)} €`],
              ].map(([k,v]) => (
                <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:13,
                  padding:'4px 0', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ color:'var(--text-3)' }}>{k}</span>
                  <span style={{ fontWeight:600 }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Add-ons */}
            {addons.length > 0 && (
              <div className="card" style={{ textAlign:'left', marginBottom:16 }}>
                <h4 style={{ fontSize:14, fontWeight:700, marginBottom:4 }}>Enrichissez votre expérience</h4>
                <p style={{ fontSize:12, color:'var(--text-3)', marginBottom:12 }}>Options disponibles sur ce site</p>
                {addons.map(addon => {
                  const sel = selectedAddons.some(a => a.id === addon.id);
                  return (
                    <div key={addon.id} onClick={() => setSelectedAddons(prev =>
                        sel ? prev.filter(a => a.id !== addon.id) : [...prev, addon])}
                      style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                        padding:'11px 14px', borderRadius:10, marginBottom:8, cursor:'pointer',
                        background: sel ? 'rgba(124,92,252,0.1)' : 'var(--bg-elevated)',
                        border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}` }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{addon.name}</div>
                        {addon.description && <div style={{ fontSize:11, color:'var(--text-3)' }}>{addon.description}</div>}
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                        <span style={{ fontWeight:700, color: sel ? 'var(--accent-light)' : 'var(--text-1)', fontSize:13 }}>
                          {addon.price.toFixed(2)} €
                        </span>
                        <div style={{ width:20, height:20, borderRadius:'50%',
                          background: sel ? 'var(--accent)' : 'var(--border)',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          color:'white', fontSize:12, fontWeight:700 }}>
                          {sel ? '✓' : '+'}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {selectedAddons.length > 0 && (
                  <div style={{ padding:'8px 12px', background:'rgba(0,210,180,0.08)',
                    border:'1px solid var(--teal)', borderRadius:8, fontSize:12, color:'var(--teal)' }}>
                    {selectedAddons.map(a => a.name).join(', ')} — à régler sur place
                  </div>
                )}
              </div>
            )}

            {/* Paiement Stripe intégré (iframe) : une fois la session créée, on
                remplace le choix de règlement + bouton par le formulaire de
                paiement affiché directement ici — pas de redirection. */}
            {checkoutClientSecret ? (
              <div className="card" style={{ marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>Paiement sécurisé</div>
                <EmbeddedStripeCheckout publishableKey={stripePubKey} clientSecret={checkoutClientSecret}/>
                <button className="btn btn-secondary btn-sm" style={{ marginTop:12 }}
                  onClick={() => setCheckoutClientSecret(null)}>
                  ← Changer de mode de règlement
                </button>
              </div>
            ) : (
              <>
                {/* Choix paiement */}
                {stripeEnabled && (
                  <div className="card" style={{ marginBottom:16 }}>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>Mode de règlement</div>
                    {(() => {
                      const total   = parseFloat(pendingFormData.totalAmount || 0);
                      const addonsT = selectedAddons.reduce((s,a)=>s+(a.price||0),0);
                      // Acompte = prix unitaire 1 place adulte
                      let deposit = 10;
                      if (pendingFormData?.priceBreakdown) {
                        const bd = typeof pendingFormData.priceBreakdown === 'string'
                          ? JSON.parse(pendingFormData.priceBreakdown)
                          : pendingFormData.priceBreakdown;
                        const adult = bd.find(b =>
                          b.profileName?.toLowerCase().includes('plein') ||
                          b.profileName?.toLowerCase().includes('adulte')
                        ) || bd[0];
                        if (adult) deposit = Math.max(parseFloat(adult.lineTotal / adult.qty), 10);
                      } else {
                        deposit = Math.max(parseFloat((total/(pendingFormData.playerCount||1)).toFixed(2)),10);
                      }
                      return (
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                          {[
                            { key:'deposit', label:'Acompte', sub:`${deposit.toFixed(2)} €`, desc:'1 place adulte' },
                            { key:'full',    label:'Total',   sub:`${(total+addonsT).toFixed(2)} €`, desc:addonsT>0?`Réservation + options`:'Règlement complet' },
                          ].map(({key,label,sub,desc}) => (
                            <div key={key} onClick={() => setPayChoice(key)}
                              style={{ padding:'14px', borderRadius:12, cursor:'pointer', textAlign:'center',
                                background: payChoice===key ? 'rgba(124,92,252,0.12)' : 'var(--bg-elevated)',
                                border: `2px solid ${payChoice===key ? 'var(--accent)' : 'var(--border)'}` }}>
                              <div style={{ fontSize:12, color:'var(--text-3)', marginBottom:4 }}>{label}</div>
                              <div style={{ fontSize:20, fontWeight:800, color: payChoice===key ? 'var(--accent-light)' : 'var(--text-1)' }}>{sub}</div>
                              <div style={{ fontSize:11, color:'var(--text-3)', marginTop:2 }}>{desc}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {!stripeEnabled && (
                  <div style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'14px 16px',
                    fontSize:14, color:'var(--text-2)', marginBottom:16 }}>
                    💳 Règlement sur place ou par téléphone
                    {businessPhone && <div style={{ marginTop:6, fontWeight:600 }}>📞 {businessPhone}</div>}
                  </div>
                )}

                <button className="btn btn-primary"
                  style={{ justifyContent:'center', width:'100%', padding:'14px', fontSize:15 }}
                  disabled={loading || stripeLoading || (stripeEnabled && !payChoice)}
                  onClick={confirmAndPay}>
                  {loading || stripeLoading
                    ? '⏳ Traitement…'
                    : stripeEnabled && payChoice
                      ? `💳 Payer et confirmer`
                      : stripeEnabled
                        ? 'Choisissez un mode de règlement'
                        : '✓ Confirmer la réservation'}
                </button>
              </>
            )}
          </div>
        )}

        {/* Étape 4 — Confirmation */}
        {step === 4 && booking && (
          <div style={{ textAlign:'center', maxWidth:500, margin:'0 auto' }}>
            <div style={{ fontSize:64, marginBottom:16 }}>🎉</div>
            <h2 style={{ fontSize:26, fontWeight:700, marginBottom:8 }}>Réservation confirmée !</h2>
            <p style={{ color:'var(--text-2)', marginBottom:24 }}>Vous recevrez un rappel par email la veille de votre session.</p>
            <div className="card" style={{ textAlign:'left', marginBottom:24 }}>
              {[
                ['Référence',  <span className="font-mono text-accent" style={{fontSize:17,fontWeight:700}}>{booking.booking_ref}</span>],
                ['Salle',      booking.room_name || selectedRoom?.name],
                ['Date',       booking.session_date ? format(new Date(booking.session_date),"EEEE d MMMM yyyy",{locale:fr}) : ''],
                ['Heure',      booking.session_start?.slice(0,5)],
                ['Joueurs',    booking.player_count],
                ['Total',      `${parseFloat(booking.total_amount).toFixed(2)} €`],
              ].map(([k,v]) => (
                <div key={k} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ color:'var(--text-2)', fontSize:14 }}>{k}</span>
                  <span style={{ fontWeight:600 }}>{v}</span>
                </div>
              ))}
            </div>

            {selectedAddons.length > 0 && (
              <div style={{ background:'rgba(0,210,180,0.08)', border:'1px solid var(--teal)',
                borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:13, color:'var(--teal)', textAlign:'left' }}>
                ✓ Options ajoutées : {selectedAddons.map(a => a.name).join(', ')} — à régler sur place
              </div>
            )}

            <div style={{ background:'rgba(0,210,180,0.1)', border:'1px solid var(--teal)',
              borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:14, color:'var(--teal)' }}>
              ✅ Paiement enregistré — réservation confirmée
            </div>

            <button className="btn btn-secondary" style={{justifyContent:'center', width:'100%'}}
              onClick={() => { setStep(0); setRoom(null); setDate(null); setSlot(null); setBooking(null); setPendingForm(null); setSelectedAddons([]); }}>
              Faire une autre réservation
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
